import React, {
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
} from "react";
import { useTranslation } from "react-i18next";
import type {
  TelemetryDataPoint,
  ChannelMetadata,
  TelemetryNote,
} from "@/types";
import { getChannelColor, type ChannelColorIndices } from "./channelColors";
import {
  CHART_CONSTANTS,
  findClosestPoint,
  findNoteAtMouse,
  getChartLayout,
  getFullTimeDomain,
  getLapBarHeight,
  type LapTiming,
} from "./chartLayout";
import {
  drawChannels,
  drawGrid,
  drawLapBar,
  drawNoteMarkers,
  drawSyncLine,
  drawXAxis,
  setupHiDpiCanvas,
  type ChannelStats,
} from "./chartRenderers";
import { useChartPanZoom } from "./useChartPanZoom";
import { useNoteDrag } from "./useNoteDrag";
import { usePinchZoom } from "./usePinchZoom";

// Defensive fallback for the empty-data case; the degenerate-domain guard in
// computePinchDomain already handles it (getFullTimeDomain([]) yields
// [Infinity, -Infinity], whose negative span triggers the early return).
const EMPTY_FULL_DOMAIN: [number, number] = [0, 0];

// Canvas-based multi-channel chart component
interface CanvasChartProps {
  data: TelemetryDataPoint[];
  selectedChannels: string[];
  channelColorIndices?: ChannelColorIndices;
  channelMetadata: Record<string, ChannelMetadata>;
  chartHeight: number;
  showGrid: boolean;
  zoomDomain: [number, number] | null;
  onZoomChange: (domain: [number, number] | null) => void;
  width: number;
  height: number;
  lapData?: LapTiming[];
  // Video synchronization props
  syncCurrentTime?: number;
  onTimeUpdate?: (time: number) => void;
  onHoverTimeUpdate?: (time: number | null) => void;
  onManualPan?: () => void;
  autoCenter?: boolean;
  onAutoCenterChange?: (enabled: boolean) => void;
  // Telemetry notes
  notes?: TelemetryNote[];
  onNoteClick?: (note: TelemetryNote) => void;
  onNoteCreate?: (timeOffset: number) => void;
  onNoteUpdate?: (noteId: string, newTimeOffset: number) => void;
  selectedNoteId?: string | null;
  notesEnabled?: boolean;
}

export const CanvasChart: React.FC<CanvasChartProps> = ({
  data,
  selectedChannels,
  channelColorIndices,
  channelMetadata,
  chartHeight,
  showGrid,
  zoomDomain,
  onZoomChange,
  width,
  height,
  lapData,
  syncCurrentTime,
  onTimeUpdate,
  onHoverTimeUpdate,
  onManualPan,
  autoCenter,
  onAutoCenterChange,
  notes = [],
  onNoteClick,
  onNoteCreate,
  onNoteUpdate,
  selectedNoteId,
  notesEnabled = false,
}) => {
  const { t } = useTranslation(["pages", "common"]);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const overlayCanvasRef = useRef<HTMLCanvasElement>(null);
  const [cursor, setCursor] = useState<{ x: number; timestamp: number } | null>(
    null,
  );
  const lastHoverTimestampRef = useRef<number | null>(null);
  const [isReceivingSync, setIsReceivingSync] = useState(false);
  const lastSyncTimeRef = useRef<number | undefined>(syncCurrentTime);
  const [clickStart, setClickStart] = useState<{
    x: number;
    time: number;
  } | null>(null);
  const [tooltip, setTooltip] = useState<{
    x: number;
    y: number;
    data: Array<{ channel: string; value: number; color: string }>;
  } | null>(null);

  // Detect when we receive a sync update and temporarily disable onTimeUpdate
  useEffect(() => {
    if (syncCurrentTime !== lastSyncTimeRef.current) {
      setIsReceivingSync(true);
      lastSyncTimeRef.current = syncCurrentTime;

      // Re-enable after a short delay
      const timer = setTimeout(() => {
        setIsReceivingSync(false);
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [syncCurrentTime]);
  // Chart dimensions and layout
  const lapBarHeight = getLapBarHeight(lapData);
  const margins = useMemo(
    () => getChartLayout(lapBarHeight).margins,
    [lapBarHeight],
  );
  const chartWidth = width - margins.left - margins.right;
  const chartAreaHeight = chartHeight - 30;

  // Pan/zoom state machine (drag-to-pan while zoomed)
  const {
    isDragging,
    realTimeDomain,
    beginPan,
    handlePanMove,
    endPan,
    cancelPan,
  } = useChartPanZoom({
    zoomDomain,
    onZoomChange,
    data,
    chartWidth,
    onManualPan,
    syncCurrentTime,
    autoCenter,
    onAutoCenterChange,
  });

  // Full data time extent; current view domain (pan overrides zoom overrides full)
  const fullTimeDomain = useMemo(() => getFullTimeDomain(data), [data]);
  const currentTimeDomain = realTimeDomain || zoomDomain || fullTimeDomain;

  const pinch = usePinchZoom({
    zoomDomain,
    fullDomain: data.length > 0 ? fullTimeDomain : EMPTY_FULL_DOMAIN,
    chartWidth,
    onZoomChange,
  });

  // Note-drag state machine (press, drag threshold, drop)
  const {
    draggingNoteId,
    noteDragStart,
    hoveredNoteId,
    beginNoteDrag,
    handleNoteDragMove,
    endNoteDrag,
    cancelPending,
    cancel: cancelNoteDrag,
    updateHover,
    clearHover,
  } = useNoteDrag({ notes, data, currentTimeDomain, onNoteUpdate });

  // Calculate domains, scales, and statistics
  const { xDomain, channelDomains, channelStats, xScale, yScales } =
    useMemo(() => {
      const timeValues = data.map((d) => d.timestamp);
      const xMin = zoomDomain?.[0] ?? Math.min(...timeValues);
      const xMax = zoomDomain?.[1] ?? Math.max(...timeValues);
      const xDomain: [number, number] = [xMin, xMax];

      const channelDomains: Record<string, [number, number]> = {};
      const channelStats: Record<string, ChannelStats> = {};
      const yScales: Record<string, (value: number) => number> = {};

      selectedChannels.forEach((channel, index) => {
        const values = data
          .map((d) => d.values[channel])
          .filter((v) => v !== undefined);
        if (values.length === 0) return;

        // Calculate actual statistics
        const min = Math.min(...values);
        const max = Math.max(...values);
        const sum = values.reduce((acc, val) => acc + val, 0);
        const mean = sum / values.length;

        channelStats[channel] = { min, max, mean };

        const range = max - min;
        const padding = range * 0.05;
        const domain: [number, number] = [min - padding, max + padding];
        channelDomains[channel] = domain;

        // Create Y scale for this channel's chart area
        const chartTop = index * chartHeight + margins.top;
        const chartBottom = chartTop + chartAreaHeight;
        yScales[channel] = (value: number) => {
          const normalized = (value - domain[0]) / (domain[1] - domain[0]);
          return chartBottom - normalized * chartAreaHeight;
        };
      });

      // X scale
      const xScale = (timestamp: number) => {
        const normalized = (timestamp - xDomain[0]) / (xDomain[1] - xDomain[0]);
        return margins.left + normalized * chartWidth;
      };

      return { xDomain, channelDomains, channelStats, xScale, yScales };
    }, [
      data,
      selectedChannels,
      zoomDomain,
      chartWidth,
      chartAreaHeight,
      chartHeight,
      margins,
    ]);

  // Drawing function: orchestrates the per-layer canvas renderers
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    setupHiDpiCanvas(canvas, ctx, width, height);

    // Dark background fill
    ctx.fillStyle = "#080810";
    ctx.fillRect(0, 0, width, height);

    drawGrid(ctx, {
      showGrid,
      xDomain,
      xScale,
      selectedChannels,
      channelDomains,
      yScales,
      margins,
      width,
      height,
    });

    drawLapBar(ctx, { lapData, lapBarHeight, xScale, t });

    // Points within the current viewport (shared across all channels)
    const viewportData = data.filter(
      (point) =>
        point.timestamp >= xDomain[0] && point.timestamp <= xDomain[1],
    );

    drawChannels(ctx, {
      data,
      viewportData,
      selectedChannels,
      channelColorIndices,
      channelMetadata,
      channelDomains,
      channelStats,
      yScales,
      xScale,
      margins,
      chartHeight,
      chartAreaHeight,
      width,
      t,
    });

    drawXAxis(ctx, { xDomain, xScale, margins, width, height });

    drawSyncLine(ctx, {
      syncCurrentTime,
      xDomain,
      xScale,
      margins,
      height,
      t,
    });

    drawNoteMarkers(ctx, {
      notes,
      currentTimeDomain,
      draggingNoteId,
      noteDragStart,
      selectedNoteId,
      hoveredNoteId,
      xScale,
      margins,
      width,
      height,
    });
  }, [
    data,
    selectedChannels,
    channelColorIndices,
    channelMetadata,
    chartHeight,
    showGrid,
    width,
    height,
    xDomain,
    channelDomains,
    channelStats,
    xScale,
    yScales,
    margins,
    chartAreaHeight,
    lapData,
    lapBarHeight,
    syncCurrentTime,
    notes,
    selectedNoteId,
    hoveredNoteId,
    currentTimeDomain,
    draggingNoteId,
    noteDragStart,
    t,
  ]);

  // Redraw when dependencies change
  useEffect(() => {
    draw();
  }, [draw]);

  // Hover cursor is drawn on a lightweight overlay so hover movement never
  // triggers a full chart redraw
  const drawCursorOverlay = useCallback(() => {
    const overlay = overlayCanvasRef.current;
    if (!overlay) return;

    const ctx = overlay.getContext("2d");
    if (!ctx) return;

    setupHiDpiCanvas(overlay, ctx, width, height);
    ctx.clearRect(0, 0, width, height);

    if (!cursor) return;

    ctx.strokeStyle = notesEnabled ? "#22c55e" : "rgba(255,255,255,0.18)";
    ctx.lineWidth = notesEnabled ? 2 : 1;
    if (!notesEnabled) {
      ctx.setLineDash([3, 3]);
    }
    ctx.beginPath();
    ctx.moveTo(cursor.x, margins.top);
    ctx.lineTo(cursor.x, height - margins.bottom);
    ctx.stroke();
    ctx.setLineDash([]);
  }, [cursor, width, height, margins, notesEnabled]);

  useEffect(() => {
    drawCursorOverlay();
  }, [drawCursorOverlay]);

  // Coalesce high-frequency mousemove events into one processing run per frame
  const pendingMouseMoveRef = useRef<{
    mouseX: number;
    mouseY: number;
    buttons: number;
  } | null>(null);
  const mouseMoveRafRef = useRef<number | null>(null);

  const cancelPendingMouseMove = useCallback(() => {
    pendingMouseMoveRef.current = null;
    if (mouseMoveRafRef.current !== null) {
      cancelAnimationFrame(mouseMoveRafRef.current);
      mouseMoveRafRef.current = null;
    }
  }, []);

  const handlePointerLeave = useCallback(() => {
    cancelPendingMouseMove();
    setCursor(null);
    setTooltip(null);
    endPan();
    clearHover();

    // Clear hover time for position tracking
    lastHoverTimestampRef.current = null;
    onHoverTimeUpdate?.(null);
  }, [cancelPendingMouseMove, endPan, clearHover, onHoverTimeUpdate]);

  const handlePointerCancel = useCallback(
    (event: React.PointerEvent<HTMLCanvasElement>) => {
      pinch.releasePointer(event.pointerId);
      cancelPendingMouseMove();
      endPan();
      clearHover();
      setCursor(null);
      setTooltip(null);
      cancelNoteDrag();
      setClickStart(null);

      // Clear hover time for position tracking (parity with pointer leave)
      lastHoverTimestampRef.current = null;
      onHoverTimeUpdate?.(null);
    },
    [
      pinch,
      cancelPendingMouseMove,
      endPan,
      clearHover,
      cancelNoteDrag,
      onHoverTimeUpdate,
    ],
  );

  // Handle right-click for note editing
  const handleContextMenu = useCallback(
    (event: React.MouseEvent<HTMLCanvasElement>) => {
      event.preventDefault(); // Prevent default context menu

      // Clear any drag state that might be active
      cancelNoteDrag();
      cancelPan();
      setClickStart(null);

      const canvas = canvasRef.current;
      if (!canvas || !notes.length || !onNoteClick || !data.length) return;

      const rect = canvas.getBoundingClientRect();
      const mouseX = event.clientX - rect.left;
      const mouseY = event.clientY - rect.top;

      // Check if right-click is near any note marker
      const hitNote = findNoteAtMouse(
        notes,
        mouseX,
        mouseY,
        currentTimeDomain,
        margins,
        chartWidth,
        CHART_CONSTANTS.NOTE_HIT_RADIUS,
      );

      if (hitNote) {
        // Right-click is on a note marker - open for editing
        onNoteClick(hitNote);
      }
    },
    [
      cancelNoteDrag,
      cancelPan,
      notes,
      onNoteClick,
      data,
      currentTimeDomain,
      margins,
      chartWidth,
    ],
  );

  // Handle pointer down for panning and click detection
  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLCanvasElement>) => {
      // Only handle left-clicks (button 0), ignore right-clicks (button 2) and middle-clicks (button 1)
      if (event.button !== 0) {
        // Clear any existing drag state when non-left buttons are pressed
        cancelNoteDrag();
        cancelPan();
        setClickStart(null);
        return;
      }

      const canvas = canvasRef.current;
      if (!canvas) return;

      const rect = canvas.getBoundingClientRect();
      const mouseX = event.clientX - rect.left;
      const mouseY = event.clientY - rect.top;

      canvas.setPointerCapture?.(event.pointerId);
      const pinchStarted = pinch.trackPointer(event.pointerId, mouseX, mouseY);

      // A second finger takes over from pan/click — pinch owns the gesture.
      if (pinchStarted) {
        cancelPendingMouseMove();
        cancelNoteDrag();
        endPan();
        clearHover();
        setClickStart(null);
        return;
      }

      // Store click start position and time for click detection
      setClickStart({ x: mouseX, time: Date.now() });

      // Clear hover state when starting interaction
      clearHover();

      // Check if pointer down is on a note marker for dragging
      const hitNote = beginNoteDrag(mouseX, mouseY, margins, chartWidth);

      if (hitNote) {
        return; // Don't start chart panning
      }

      // Only allow panning when zoomed and not dragging a note
      if (zoomDomain && !draggingNoteId) {
        beginPan(mouseX);
      }
    },
    [
      cancelNoteDrag,
      cancelPan,
      cancelPendingMouseMove,
      endPan,
      clearHover,
      beginNoteDrag,
      beginPan,
      margins,
      chartWidth,
      zoomDomain,
      draggingNoteId,
      pinch,
    ],
  );

  // Handle pointer up
  const handlePointerUp = useCallback(
    (event: React.PointerEvent<HTMLCanvasElement>) => {
      cancelPendingMouseMove();
      if (pinch.pinchActive) {
        // A pinch just ended by lifting one finger. Re-seed pan from the
        // remaining pointer so zoom-then-pan continues in one motion.
        const remaining = pinch.releasePointer(event.pointerId);
        if (remaining && zoomDomain && !draggingNoteId) {
          beginPan(remaining.x);
        }
        return;
      }
      pinch.releasePointer(event.pointerId);
      const canvas = canvasRef.current;

      // Handle note drag completion (only if we were actually dragging)
      if (canvas) {
        const rect = canvas.getBoundingClientRect();
        const mouseX = event.clientX - rect.left;

        if (endNoteDrag(mouseX, margins, chartWidth)) {
          setClickStart(null);
          endPan(); // Clear real-time domain when a note drag completes
          return;
        }
      }
      // Just clicked on a note without dragging - clear state but don't update position
      cancelPending();

      if (!canvas || !clickStart) {
        endPan(); // Clear real-time domain on early return
        clearHover(); // Clear hover state on early return
        // Clear note dragging state on early return
        cancelNoteDrag();
        setClickStart(null);
        return;
      }

      const rect = canvas.getBoundingClientRect();
      const mouseX = event.clientX - rect.left;
      const timeDiff = Date.now() - clickStart.time;
      const distanceDiff = Math.abs(mouseX - clickStart.x);

      // Detect click (short time, small movement)
      const isClick = timeDiff < 200 && distanceDiff < 5;

      if (isClick && data.length > 0 && !isReceivingSync) {
        // Handle regular click for time seeking or note creation
        const normalizedX = (mouseX - margins.left) / chartWidth;
        const timestamp =
          currentTimeDomain[0] +
          normalizedX * (currentTimeDomain[1] - currentTimeDomain[0]);

        // Find closest data point
        const closestPoint = findClosestPoint(data, timestamp);

        // Handle note creation if enabled
        if (notesEnabled && onNoteCreate) {
          onNoteCreate(closestPoint.timestamp);
        } else {
          onTimeUpdate?.(closestPoint.timestamp);
        }
      }

      endPan(); // Clear real-time domain when drag ends
      setClickStart(null);
      // Note: Don't clear hover state here as mouse might still be over a note
    },
    [
      endNoteDrag,
      cancelPending,
      endPan,
      cancelNoteDrag,
      clearHover,
      clickStart,
      zoomDomain,
      draggingNoteId,
      beginPan,
      data,
      margins,
      chartWidth,
      currentTimeDomain,
      isReceivingSync,
      onTimeUpdate,
      notesEnabled,
      onNoteCreate,
      cancelPendingMouseMove,
      pinch,
    ],
  );

  // Regular cursor tracking / hover processing, coalesced via RAF in enhancedPointerMove
  const processMouseMove = useCallback(
    (mouseX: number, mouseY: number, mouseButtons: number) => {
      // Handle panning when dragging - only while the pointer is pressed
      if (handlePanMove(mouseX, mouseButtons !== 0)) return;

      // Handle note dragging - only if left mouse button is pressed
      if (handleNoteDragMove(mouseX, mouseY, mouseButtons)) return;

      // Regular cursor tracking when not dragging
      if (!isDragging) {
        // Check if hovering over note markers first
        updateHover(mouseX, mouseY, margins, chartWidth);

        // Calculate timestamp from mouse position
        const normalizedX = (mouseX - margins.left) / chartWidth;
        const timestamp = xDomain[0] + normalizedX * (xDomain[1] - xDomain[0]);

        // Find closest data point
        const closestPoint = findClosestPoint(data, timestamp);

        if (closestPoint) {
          setCursor({
            x: xScale(closestPoint.timestamp),
            timestamp: closestPoint.timestamp,
          });

          // Call hover time update callback for track position updates
          if (closestPoint.timestamp !== lastHoverTimestampRef.current) {
            lastHoverTimestampRef.current = closestPoint.timestamp;
            onHoverTimeUpdate?.(closestPoint.timestamp);
          }

          // Prepare tooltip data
          const tooltipData = selectedChannels
            .map((channel, index) => ({
              channel,
              value: closestPoint.values[channel],
              color: getChannelColor(channel, channelColorIndices, index),
            }))
            .filter((item) => item.value !== undefined);

          setTooltip({ x: mouseX, y: mouseY, data: tooltipData });
        }
      }
    },
    [
      handlePanMove,
      handleNoteDragMove,
      updateHover,
      isDragging,
      margins,
      chartWidth,
      xDomain,
      xScale,
      data,
      selectedChannels,
      channelColorIndices,
      onHoverTimeUpdate,
    ],
  );

  const enhancedPointerMove = useCallback(
    (event: React.PointerEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const rect = canvas.getBoundingClientRect();

      // An active pinch owns the gesture; skip pan/hover entirely.
      if (
        pinch.movePointer(
          event.pointerId,
          event.clientX - rect.left,
          event.clientY - rect.top,
        )
      ) {
        return;
      }

      pendingMouseMoveRef.current = {
        mouseX: event.clientX - rect.left,
        mouseY: event.clientY - rect.top,
        buttons: event.buttons,
      };

      if (mouseMoveRafRef.current !== null) return;

      mouseMoveRafRef.current = requestAnimationFrame(() => {
        mouseMoveRafRef.current = null;
        const pending = pendingMouseMoveRef.current;
        pendingMouseMoveRef.current = null;
        if (pending) {
          processMouseMove(pending.mouseX, pending.mouseY, pending.buttons);
        }
      });
    },
    [processMouseMove, pinch],
  );

  useEffect(() => {
    return () => {
      cancelPendingMouseMove();
    };
  }, [cancelPendingMouseMove]);

  // Add global pointer end listener for better drag handling
  useEffect(() => {
    if (isDragging || draggingNoteId) {
      const handleGlobalMouseUp = () => {
        cancelPendingMouseMove();
        endPan(); // Clear real-time domain on global pointer end
        clearHover(); // Clear hover state on global pointer end
        cancelNoteDrag(); // Clear note dragging state on global pointer end
      };

      document.addEventListener("pointerup", handleGlobalMouseUp);
      document.addEventListener("pointercancel", handleGlobalMouseUp);
      return () => {
        document.removeEventListener("pointerup", handleGlobalMouseUp);
        document.removeEventListener("pointercancel", handleGlobalMouseUp);
      };
    }
  }, [
    isDragging,
    draggingNoteId,
    cancelPendingMouseMove,
    endPan,
    clearHover,
    cancelNoteDrag,
  ]);

  return (
 <div className="relative">
      <canvas
        data-testid="telemetry-canvas"
        ref={canvasRef}
        width={width}
        height={height}
        onPointerMove={enhancedPointerMove}
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerLeave}
        onPointerCancel={handlePointerCancel}
        onContextMenu={handleContextMenu}
 className={`select-none ${
          draggingNoteId
            ? "cursor-grabbing"
            : isDragging
              ? "cursor-grabbing"
              : hoveredNoteId && onNoteUpdate
                ? "cursor-grab"
                : hoveredNoteId
                  ? "cursor-context-menu"
                  : zoomDomain
                    ? "cursor-grab"
                    : "cursor-crosshair"
        }`}
        style={{
          width: `${width}px`,
          height: `${height}px`,
          touchAction: "pan-y",
        }}
      />

      <canvas
        data-testid="telemetry-canvas-overlay"
        ref={overlayCanvasRef}
        aria-hidden="true"
        style={{
          pointerEvents: "none",
          position: "absolute",
          top: 0,
          left: 0,
          width: `${width}px`,
          height: `${height}px`,
        }}
      />

      {/* Tooltip */}
      {tooltip && cursor && (
        <div
          className="absolute pointer-events-none z-10"
          style={{
            left: tooltip.x + 10,
            top: tooltip.y - 10,
            transform: tooltip.x > width - 200 ? "translateX(-100%)" : undefined,
            background: "rgba(15,15,30,0.92)",
            border: "1px solid #1e2a4a",
            borderRadius: 10,
            padding: "10px 14px",
            minWidth: 150,
            boxShadow: "0 8px 24px rgba(0,0,0,0.6)",
            backdropFilter: "blur(8px)",
          }}
        >
          <div
            style={{
              fontFamily: "monospace",
              fontSize: 11,
              color: "#374151",
              marginBottom: 8,
              paddingBottom: 6,
              borderBottom: "1px solid #1a1a2e",
            }}
          >
            {cursor.timestamp.toFixed(2)}s
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {tooltip.data.map(({ channel, value, color }) => {
              const unit = channelMetadata[channel]?.unit || "";
              return (
                <div key={channel} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <div
                      style={{
                        width: 7,
                        height: 7,
                        borderRadius: "50%",
                        background: color,
                        boxShadow: `0 0 4px ${color}`,
                        flexShrink: 0,
                      }}
                    />
                    <span style={{ fontSize: 11, color: "#6b7280" }}>{channel}</span>
                  </div>
                  <span style={{ fontSize: 12, fontFamily: "monospace", fontWeight: 600, color: "#e2e8f0" }}>
                    {value.toFixed(3)} {unit}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
